/**
 * Service API Client Unifié
 * 
 * Service centralisé pour toutes les communications avec le backend Django.
 * Remplace: api.ts, djangoApi.ts, jsonServerApi.ts
 * 
 * Fonctionnalités:
 * - Authentification JWT automatique
 * - Refresh token automatique
 * - Gestion des erreurs centralisée
 * - Support upload/download fichiers
 * - Configuration par variables d'environnement
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

// Configuration de l'URL de base depuis les variables d'environnement
const getBaseURL = (): string => {
  // Utiliser le proxy Vite configuré (/api/v1 -> http://localhost:8000/api/v1)
  const baseUrl = import.meta.env.VITE_ERP_API_URL || import.meta.env.VITE_API_BASE_URL || '/api/v1'
  console.log('🔧 ApiClient baseURL:', baseUrl)
  return baseUrl
}

class ApiClient {
  private axiosInstance: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: () => void
    reject: (reason?: any) => void
  }> = []

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: getBaseURL(),
      timeout: 15000,
      withCredentials: true,   // Envoie automatiquement les cookies httpOnly
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })

    this.setupInterceptors()
  }

  /**
   * Configuration des intercepteurs Axios
   */
  private setupInterceptors(): void {
    // Intercepteur de requête
    // Les cookies httpOnly sont envoyés automatiquement via withCredentials
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Si c'est un FormData, laisser le navigateur définir le Content-Type
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }

        // Log en développement uniquement
        if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_API_LOGS === 'true') {
          console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
        }

        return config
      },
      (error) => {
        if (import.meta.env.DEV) console.error('[API] Request error:', error)
        return Promise.reject(error)
      }
    )

    // Intercepteur de réponse - Gérer les erreurs et refresh token
    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (import.meta.env.VITE_ENABLE_API_LOGS === 'true') {
          console.log(`✅ API Response [${response.status}]:`, response.data)
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Retry automatique sur 502/503 pour les requêtes GET (idempotentes)
        const status = error.response?.status
        const isSafeMethod = ['get', 'head'].includes((originalRequest?.method || '').toLowerCase())
        if ((status === 502 || status === 503) && isSafeMethod && !originalRequest._retryCount) {
          originalRequest._retryCount = 0
        }
        if ((status === 502 || status === 503) && isSafeMethod && originalRequest._retryCount < 3) {
          originalRequest._retryCount = (originalRequest._retryCount || 0) + 1
          const delay = originalRequest._retryCount * 1000
          await new Promise(resolve => setTimeout(resolve, delay))
          return this.axiosInstance(originalRequest)
        }

        // Gestion erreur 401 - Token expiré
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Une requête de refresh est déjà en cours, mettre en attente
            return new Promise<void>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            }).then(() => {
              return this.axiosInstance(originalRequest)
            }).catch((err) => {
              return Promise.reject(err)
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            // Le cookie refresh est envoyé automatiquement via withCredentials
            await axios.post(`${getBaseURL()}/auth/token/refresh/`, {}, {
              withCredentials: true
            })

            // Résoudre toutes les requêtes en attente
            this.processQueue(null)

            // Retry la requête originale — le nouveau cookie access est envoyé automatiquement
            return this.axiosInstance(originalRequest)

          } catch (refreshError) {
            this.processQueue(refreshError)
            this.handleLogout()
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }

        // Autres erreurs
        console.error('❌ API Error:', error.response?.data || error.message)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Traiter les requêtes en attente après refresh token
   */
  private processQueue(error: any): void {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve()
      }
    })
    this.failedQueue = []
  }

  /**
   * Déconnexion et redirection
   */
  private handleLogout(): void {
    localStorage.removeItem('user')
    window.location.href = '/login'
  }

  // ========================================
  // Méthodes HTTP de base
  // ========================================

  async get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get(url, { params, ...config })
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete(url, config)
  }

  // ========================================
  // Méthodes d'authentification
  // ========================================

  async login(credentials: { username: string; password: string }) {
    try {
      const response = await this.post('/auth/login/', credentials)
      const { user } = response.data
      // Tokens reçus en cookies httpOnly — seule la donnée utilisateur (non-sensible) est stockée
      localStorage.setItem('user', JSON.stringify(user))
      return response.data
    } catch (error: any) {
      if (import.meta.env.DEV) console.error('❌ Erreur de connexion:', error.response?.data || error.message)
      throw new Error(error.response?.data?.detail || 'Erreur de connexion')
    }
  }

  async logout() {
    try {
      // Le cookie refresh est envoyé automatiquement via withCredentials
      await this.post('/auth/logout/', {}).catch(() => {
        if (import.meta.env.DEV) console.warn('⚠️ Erreur logout serveur (ignorée)')
      })
    } finally {
      this.handleLogout()
    }
  }

  async getCurrentUser() {
    const response = await this.get('/auth/profile/')
    const user = response.data
    localStorage.setItem('user', JSON.stringify(user))
    return user
  }

  async refreshToken() {
    const response = await axios.post(`${getBaseURL()}/auth/token/refresh/`, {}, {
      withCredentials: true
    })
    return response.data.access
  }

  // ========================================
  // Méthodes utilitaires
  // ========================================

  /**
   * Upload de fichier avec progression
   */
  async uploadFile(
    url: string,
    file: File,
    fieldName: string = 'file',
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse> {
    const formData = new FormData()
    formData.append(fieldName, file)

    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  }

  /**
   * Download de fichier
   */
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.axiosInstance.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /**
   * Test de connexion au backend
   */
  async testConnection() {
    try {
      const response = await this.get('/health/')
      return { success: true, data: response.data }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  }

  /**
   * Obtenir l'URL de base configurée
   */
  getBaseURL(): string {
    return this.axiosInstance.defaults.baseURL || ''
  }
}

// Export de l'instance unique
export const apiClient = new ApiClient()
export default apiClient
